import "./App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { AuthProvider, useAuth } from "./contexts/AuthContext";

import Analytics from "./components/Analytics";
import WhatsAppHelpWidget from "./components/WhatsAppHelpWidget";
import CookieConsent from "./components/CookieConsent";
import SEOHead from "./components/SEOHead";
import InstallPrompt from "./components/InstallPrompt";
import PopupBannerHost from "./components/PopupBannerHost";

import HomePage from "./pages/public/HomePage";
import SpeakersPage from "./pages/public/SpeakersPage";
import ProgramPage from "./pages/public/ProgramPage";
import FairPage from "./pages/public/FairPage";
import VerifyPage from "./pages/public/VerifyPage";
import RegisterPage from "./pages/public/RegisterPage";
import VisitorRegisterPage from "./pages/public/VisitorRegisterPage";
import ExhibitorRegisterPage from "./pages/public/ExhibitorRegisterPage";
import SpeakerApplicationPage from "./pages/public/SpeakerApplicationPage";
import StaffScanPage from "./pages/public/StaffScanPage";
import InvestmentGamePage from "./pages/public/InvestmentGamePage";
import PastEventsPage from "./pages/public/PastEventsPage";
import BlogPage from "./pages/public/BlogPage";
import BlogDetailPage from "./pages/public/BlogDetailPage";
import SeminarHomePage from "./pages/public/SeminarHomePage";
import SeminarDetailPage from "./pages/public/SeminarDetailPage";
import BultenPage from "./pages/public/BultenPage";
import ZirveAilesiPage from "./pages/public/ZirveAilesiPage";
import GalleryPage from "./pages/public/GalleryPage";
import PrivacyPage from "./pages/public/PrivacyPage";
import KvkkPage from "./pages/public/KvkkPage";

import AdminLogin from "./pages/admin/AdminLogin";
import AdminLayout from "./pages/admin/AdminLayout";
import AdminDashboard from "./pages/admin/AdminDashboard";
import MemberList from "./pages/admin/MemberList";
import GuestList from "./pages/admin/GuestList";
import ExhibitorList from "./pages/admin/ExhibitorList";
import SpeakerApplicationList from "./pages/admin/SpeakerApplicationList";
import AdminUsers from "./pages/admin/AdminUsers";
import SpeakerManagement from "./pages/admin/SpeakerManagement";
import SponsorManagement from "./pages/admin/SponsorManagement";
import SponsorPackagesManagement from "./pages/admin/SponsorPackagesManagement";
import BannerManagement from "./pages/admin/BannerManagement";
import BlogManagement from "./pages/admin/BlogManagement";
import EventManagement from "./pages/admin/EventManagement";
import ProgramManagement from "./pages/admin/ProgramManagement";
import AdminSEO from "./pages/admin/AdminSEO";
import HeroSlidesManagement from "./pages/admin/HeroSlidesManagement";
import FairManagement from "./pages/admin/FairManagement";
import SiteSettingsManagement from "./pages/admin/SiteSettingsManagement";
import CheckInPage from "./pages/admin/CheckInPage";
import InviteCodesManagement from "./pages/admin/InviteCodesManagement";
import ApiKeysManagement from "./pages/admin/ApiKeysManagement";
import VisitegoSync from "./pages/admin/VisitegoSync";
import InvestmentGameList from "./pages/admin/InvestmentGameList";
import IntegrationsPage from "./pages/admin/IntegrationsPage";
import AcademyManagement from "./pages/admin/AcademyManagement";
import NewsletterManagement from "./pages/admin/NewsletterManagement";
import FamilyPageManagement from "./pages/admin/FamilyPageManagement";
import GalleryManagement from "./pages/admin/GalleryManagement";
import ExpertLogin from "./pages/expert/ExpertLogin";
import ExpertGameList from "./pages/expert/ExpertGameList";
import ExpertGameDetail from "./pages/expert/ExpertGameDetail";
import ExpertPresentationMode from "./pages/expert/ExpertPresentationMode";

function AdminRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return (
    <div className="min-h-screen bg-white flex items-center justify-center">
      <div className="w-8 h-8 border-2 border-summit-navy border-t-transparent rounded-full animate-spin" />
    </div>
  );
  if (!user || user.role !== "admin") return <Navigate to="/admin/login" replace />;
  return children;
}

function App() {
  return (
    <HelmetProvider>
      <AuthProvider>
        <div className="App">
          <BrowserRouter>
            <Analytics />
            <SEOHead />
            <ConditionalHelpWidget />
            <Routes>
            {/* Public */}
            <Route path="/" element={<HomePage />} />
            <Route path="/konusmacilar" element={<SpeakersPage />} />
            <Route path="/program" element={<ProgramPage />} />
            <Route path="/fuar-alani" element={<FairPage />} />
            <Route path="/dogrulama" element={<VerifyPage />} />
            <Route path="/etkinlikler" element={<PastEventsPage />} />
            <Route path="/blog" element={<BlogPage />} />
            <Route path="/blog/:slug" element={<BlogDetailPage />} />
            <Route path="/akademi" element={<Navigate to="/seminer" replace />} />
            <Route path="/seminer" element={<SeminarHomePage />} />
            <Route path="/seminer/:slug" element={<SeminarDetailPage />} />
            <Route path="/bulten" element={<BultenPage />} />
            <Route path="/zirve-ailesi" element={<ZirveAilesiPage />} />
            <Route path="/galeri" element={<GalleryPage />} />
            <Route path="/gizlilik" element={<PrivacyPage />} />
            <Route path="/kvkk" element={<KvkkPage />} />

            {/* Registration Forms */}
            <Route path="/ziyaretci-kaydi" element={<VisitorRegisterPage />} />
            <Route path="/zirve-kaydi" element={<VisitorRegisterPage />} />
            <Route path="/fuar-kaydi" element={<VisitorRegisterPage />} />
            <Route path="/seminer-kaydi" element={<VisitorRegisterPage />} />
            <Route path="/fuar-stant-kaydi" element={<ExhibitorRegisterPage />} />
            <Route path="/konusmaci-basvuru" element={<SpeakerApplicationPage />} />
            <Route path="/bulten" element={<RegisterPage />} />

            {/* Public staff scanner (token in URL) */}
            <Route path="/tarama/:apiKey" element={<StaffScanPage />} />

            {/* Investment Mini-Game */}
            <Route path="/yatirim-oyunu" element={<InvestmentGamePage />} />

            {/* Expert Panel — for invited evaluators */}
            <Route path="/uzman/giris" element={<ExpertLogin />} />
            <Route path="/uzman" element={<Navigate to="/uzman/yatirim-oyunu" replace />} />
            <Route path="/uzman/yatirim-oyunu" element={<ExpertGameList mode="pending" />} />
            <Route path="/uzman/yorumlananlar" element={<ExpertGameList mode="commented" />} />
            <Route path="/uzman/yatirim-oyunu/:id" element={<ExpertGameDetail />} />
            <Route path="/uzman/sunum/:id" element={<ExpertPresentationMode />} />

            {/* Backward compat redirects */}
            <Route path="/uyelik" element={<Navigate to="/ziyaretci-kaydi" replace />} />

            {/* Admin */}
            <Route path="/admin/login" element={<AdminLogin />} />
            <Route path="/admin" element={<AdminRoute><AdminLayout /></AdminRoute>}>
              <Route index element={<AdminDashboard />} />
              <Route path="checkin" element={<CheckInPage />} />
              <Route path="davet-kodlari" element={<InviteCodesManagement />} />
              <Route path="api-anahtarlari" element={<ApiKeysManagement />} />
              <Route path="visitego" element={<VisitegoSync />} />
              <Route path="yatirim-oyunu" element={<InvestmentGameList />} />
              <Route path="entegrasyonlar" element={<IntegrationsPage />} />
              <Route path="ziyaretciler" element={<GuestList />} />
              <Route path="zirve-ziyaretcileri" element={
                <GuestList forcedVisitType="summit" title="Zirve Ziyaretçileri"
                  subtitle="Sadece Arsa Yatırım Zirvesi 2026 konferans programına kaydolanlar" />
              } />
              <Route path="fuar-ziyaretcileri" element={
                <GuestList forcedVisitType="fair" title="Fuar Ziyaretçileri"
                  subtitle="Sadece 8. Gayrimenkul Proje Yatırım Fuarı'na gelen ziyaretçiler" />
              } />
              <Route path="seminer-kayitlari" element={
                <GuestList forcedVisitType="seminar" title="Seminer Kayıtları"
                  subtitle="Arsa Yatırım Eğitim seminerlerine kayıt olanlar" />
              } />
              <Route path="fuar-stant" element={<ExhibitorList />} />
              <Route path="konusmaci-basvuru" element={<SpeakerApplicationList />} />
              <Route path="bulten-uyeleri" element={<MemberList />} />
              <Route path="konusmacilar" element={<SpeakerManagement />} />
              <Route path="sponsorlar" element={<SponsorManagement />} />
              <Route path="sponsor-paketleri" element={<SponsorPackagesManagement />} />
              <Route path="bannerlar" element={<BannerManagement />} />
              <Route path="akademi" element={<Navigate to="/admin/seminer" replace />} />
              <Route path="seminer" element={<AcademyManagement />} />
              <Route path="bulten" element={<NewsletterManagement />} />
              <Route path="zirve-ailesi" element={<FamilyPageManagement />} />
              <Route path="galeri" element={<GalleryManagement />} />
              <Route path="blog" element={<BlogManagement />} />
              <Route path="etkinlikler" element={<EventManagement />} />
              <Route path="program" element={<ProgramManagement />} />
              <Route path="kullanicilar" element={<AdminUsers />} />
              <Route path="seo" element={<AdminSEO />} />
              <Route path="hero-slides" element={<HeroSlidesManagement />} />
              <Route path="etkinlik-ayarlari" element={<SiteSettingsManagement />} />
              <Route path="fuar" element={<FairManagement />} />
              {/* Backward compat for old URLs */}
              <Route path="uyeler" element={<Navigate to="/admin/bulten-uyeleri" replace />} />
              <Route path="misafirler" element={<Navigate to="/admin/ziyaretciler" replace />} />
            </Route>

            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          <CookieConsent />
          <InstallPrompt />
          <PopupBannerHost />
        </BrowserRouter>
      </div>
    </AuthProvider>
    </HelmetProvider>
  );
}

export default App;

// Show WhatsApp help widget only on public pages, not in admin / scanner
function ConditionalHelpWidget() {
  const location = useLocation();
  if (location.pathname.startsWith("/admin")) return null;
  if (location.pathname.startsWith("/tarama/")) return null;
  return <WhatsAppHelpWidget />;
}
